import {
  Document, Packer, Paragraph, TextRun, ExternalHyperlink, ImageRun, Table, TableRow, TableCell,
  WidthType, AlignmentType, HeadingLevel, Header, Footer, PageNumber, LevelFormat, ShadingType,
  BorderStyle, PageOrientation, PageBreak, Tab,
  CommentRangeStart, CommentRangeEnd, CommentReference, InsertedTextRun, DeletedTextRun,
} from "docx";
import { PAGE_SIZES, dims, themeOf } from "./pages";
import { staticize } from "./render";

const BLOCK = new Set(["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "UL", "OL", "LI", "TABLE", "PRE", "HR", "SECTION", "ARTICLE"]);
const HEAD = { H1: HeadingLevel.HEADING_1, H2: HeadingLevel.HEADING_2, H3: HeadingLevel.HEADING_3, H4: HeadingLevel.HEADING_4, H5: HeadingLevel.HEADING_5, H6: HeadingLevel.HEADING_6 };
const ALIGN = { center: AlignmentType.CENTER, right: AlignmentType.RIGHT, justify: AlignmentType.JUSTIFIED, left: AlignmentType.LEFT };
const BSTYLE = { dashed: BorderStyle.DASHED, dotted: BorderStyle.DOTTED, double: BorderStyle.DOUBLE, none: BorderStyle.NONE };
const TW = 15;

const toHex = (c) => {
  if (!c) return undefined;
  if (c.startsWith("#")) {
    let h = c.slice(1);
    if (h.length === 3) h = h.split("").map((x) => x + x).join("");
    return h.slice(0, 6).toUpperCase();
  }
  const m = c.match(/rgba?\(([^)]+)\)/);
  if (!m) return undefined;
  const p = m[1].split(",").map((x) => parseFloat(x));
  if (p.length === 4 && p[3] === 0) return undefined;
  return p.slice(0, 3).map((v) => Math.round(v).toString(16).padStart(2, "0")).join("").toUpperCase();
};
const toPt = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return undefined;
  if (v.endsWith("pt")) return n;
  if (v.endsWith("px")) return n * 0.75;
  if (v.endsWith("em")) return n * 11;
  return undefined;
};
const firstFont = (f) => (f ? f.split(",")[0].replace(/["']/g, "").trim() : undefined);

function runStyle(el, st) {
  const s = { ...st };
  const t = el.tagName;
  const cs = el.style;
  if (t === "B" || t === "STRONG" || t === "TH") s.bold = true;
  if (t === "I" || t === "EM") s.italics = true;
  if (t === "U") s.underline = {};
  if (t === "S" || t === "STRIKE" || t === "DEL") s.strike = true;
  if (t === "SUP") s.superScript = true;
  if (t === "SUB") s.subScript = true;
  if (t === "FONT") {
    if (el.color) s.color = toHex(el.color);
    if (el.face) s.font = firstFont(el.face);
  }
  if (cs.fontWeight === "bold" || parseInt(cs.fontWeight) >= 600) s.bold = true;
  if (cs.fontStyle === "italic") s.italics = true;
  const td = cs.textDecorationLine || cs.textDecoration || "";
  if (td.includes("underline")) s.underline = {};
  if (td.includes("line-through")) s.strike = true;
  if (cs.color) s.color = toHex(cs.color) || s.color;
  const bg = toHex(cs.backgroundColor);
  if (bg && t !== "TD" && t !== "TH") s.shading = { type: ShadingType.CLEAR, color: "auto", fill: bg };
  if (cs.fontSize) { const p = toPt(cs.fontSize); if (p) s.size = Math.round(p * 2); }
  if (cs.fontFamily) s.font = firstFont(cs.fontFamily);
  if (cs.letterSpacing) { const p = toPt(cs.letterSpacing); if (p) s.characterSpacing = Math.round(p * 20); }
  return s;
}

function textRuns(text, st, ctx) {
  text = text.replace(/\u200B/g, "");
  if (!text) return [];
  if (st.__tc) {
    const { __tc, ...rest } = st;
    const Run = __tc.kind === "ins" ? InsertedTextRun : DeletedTextRun;
    return [new Run({ ...rest, text: text.replace(/\t/g, " "), id: ++ctx.rev, author: __tc.author, date: __tc.date })];
  }
  if (!text.includes("\t")) return [new TextRun({ ...st, text })];
  const children = [];
  text.split("\t").forEach((p, i) => { if (i) children.push(new Tab()); if (p) children.push(p); });
  return [new TextRun({ ...st, children })];
}

function imgRun(img, ctx) {
  const info = ctx.images.get(img.src);
  if (!info) return [];
  let w = info.w;
  const sw = img.style.width;
  if (sw.endsWith("%")) w = (ctx.cw * parseFloat(sw)) / 100;
  else if (sw.endsWith("px")) w = parseFloat(sw);
  else if (img.getAttribute("width")) w = parseFloat(img.getAttribute("width"));
  w = Math.min(w, ctx.cw);
  const h = (w * info.h) / info.w;
  return [new ImageRun({ type: info.type, data: info.data, transformation: { width: Math.round(w), height: Math.round(h) } })];
}

function inline(node, st, ctx) {
  if (node.nodeType === 3) return textRuns(node.data, st, ctx);
  if (node.nodeType !== 1) return [];
  const t = node.tagName;
  if (t === "BR") return [new TextRun({ ...st, break: 1 })];
  if (t === "IMG") return imgRun(node, ctx);
  if (node.classList.contains("sig-field")) {
    const img = node.querySelector("img");
    const cap = node.querySelector(".sig-caption")?.textContent;
    const runs = img ? imgRun(img, ctx) : [new TextRun({ ...st, text: "Signature: ______________________" })];
    if (cap && img) runs.push(new TextRun({ ...st, size: 18, break: 1, text: cap }));
    return runs;
  }
  if (node.classList.contains("tc-ins") || node.classList.contains("tc-del")) {
    const tc = { kind: node.classList.contains("tc-ins") ? "ins" : "del", author: node.dataset.author || "Author", date: new Date(Number(node.dataset.time) || Date.now()).toISOString() };
    return [...node.childNodes].flatMap((c) => inline(c, { ...st, __tc: tc }, ctx));
  }
  if (node.classList.contains("cmt") && ctx.cmt.ids.has(node.dataset.cid)) {
    const cid = node.dataset.cid;
    const id = ctx.cmt.ids.get(cid);
    const k = (ctx.cmt.seen[cid] = (ctx.cmt.seen[cid] || 0) + 1);
    const kids = [...node.childNodes].flatMap((c) => inline(c, st, ctx));
    return [
      ...(k === 1 ? [new CommentRangeStart(id)] : []), ...kids,
      ...(k === ctx.cmt.total[cid] ? [new CommentRangeEnd(id), new TextRun({ children: [new CommentReference(id)] })] : []),
    ];
  }
  const s = runStyle(node, st);
  if (t === "A") {
    const kids = [...node.childNodes].flatMap((c) => inline(c, { ...s, color: "0563C1", underline: {} }, ctx));
    return [new ExternalHyperlink({ link: node.href, children: kids })];
  }
  return [...node.childNodes].flatMap((c) => inline(c, s, ctx));
}

function paraProps(el, extra) {
  const cs = el.style || {};
  const p = { ...extra };
  const al = cs.textAlign || el.getAttribute?.("align");
  if (ALIGN[al]) p.alignment = ALIGN[al];
  const ml = parseFloat(cs.marginLeft) || parseFloat(cs.paddingLeft) || 0;
  if (ml && !extra.numbering && !extra.bullet) p.indent = { left: Math.round(ml * TW) };
  const lh = parseFloat(cs.lineHeight);
  p.spacing = { after: 120 };
  if (lh && !String(cs.lineHeight).endsWith("px")) p.spacing.line = Math.round(240 * lh);
  if (HEAD[el.tagName]) p.heading = HEAD[el.tagName];
  return p;
}

function group(el, st, ctx, out, extra = {}, list = null) {
  let buf = [];
  let hadBlock = false;
  const flush = () => {
    if (buf.length) out.push(new Paragraph({ ...paraProps(el, extra), children: buf }));
    buf = [];
  };
  for (const n of [...el.childNodes]) {
    if (n.nodeType === 1 && (BLOCK.has(n.tagName) || n.classList.contains("page-break"))) {
      flush();
      hadBlock = true;
      block(n, st, ctx, out, list);
    } else if (n.nodeType === 1 && n.tagName === "BR" && n === el.lastChild) continue;
    else if (n.nodeType === 3 && !n.data.trim() && !buf.length) continue;
    else buf.push(...inline(n, st, ctx));
  }
  if (buf.length) flush();
  else if (!hadBlock && el !== ctx.root) out.push(new Paragraph({ ...paraProps(el, extra), children: [] }));
}

function table(el, st, ctx) {
  const rows = [...el.rows].map((tr) => new TableRow({
    tableHeader: tr.parentElement.tagName === "THEAD",
    children: [...tr.cells].map((td) => {
      const out = [];
      group(td, runStyle(td, st), ctx, out);
      if (!out.length) out.push(new Paragraph({}));
      const bg = toHex(td.style.backgroundColor);
      const bc = toHex(td.style.borderColor) || "94A3B8";
      const bw = parseFloat(td.style.borderWidth) || 1;
      const b = { style: BSTYLE[td.style.borderStyle] || BorderStyle.SINGLE, size: Math.max(2, Math.round(bw * 6)), color: bc };
      return new TableCell({
        children: out, columnSpan: td.colSpan, rowSpan: td.rowSpan,
        shading: bg ? { fill: bg, type: ShadingType.CLEAR, color: "auto" } : undefined,
        borders: { top: b, bottom: b, left: b, right: b },
        margins: { top: 60, bottom: 60, left: 100, right: 100 },
      });
    }),
  }));
  return new Table({ rows, width: { size: 100, type: WidthType.PERCENTAGE } });
}

function block(el, st, ctx, out, list) {
  const t = el.tagName;
  if (el.classList.contains("page-break")) return out.push(new Paragraph({ children: [new PageBreak()] }));
  if (t === "HR") return out.push(new Paragraph({ border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: "999999", space: 1 } } }));
  if (t === "TABLE") return out.push(table(el, st, ctx), new Paragraph({}));
  if (t === "UL" || t === "OL") {
    const level = Math.min(list ? list.level + 1 : 0, 8);
    const inst = ++ctx.inst;
    for (const li of [...el.children]) {
      const extra = t === "OL" ? { numbering: { reference: "ol", level, instance: inst } } : { bullet: { level } };
      group(li, runStyle(li, st), ctx, out, extra, { level });
    }
    return;
  }
  group(el, runStyle(el, st), ctx, out, {}, list);
}

async function imgData(src) {
  const im = await new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const m = src.match(/^data:image\/(png|jpe?g|gif|bmp);base64,/);
  if (m) {
    return { type: m[1].startsWith("jp") ? "jpg" : m[1], data: Uint8Array.from(atob(src.split(",")[1]), (c) => c.charCodeAt(0)), w: im.naturalWidth, h: im.naturalHeight };
  }
  const c = document.createElement("canvas");
  c.width = im.naturalWidth;
  c.height = im.naturalHeight;
  c.getContext("2d").drawImage(im, 0, 0);
  const blob = await new Promise((r) => c.toBlob(r, "image/png"));
  return { type: "png", data: new Uint8Array(await blob.arrayBuffer()), w: im.naturalWidth, h: im.naturalHeight };
}

async function loadImages(roots) {
  const map = new Map();
  for (const r of roots) for (const img of r.querySelectorAll("img")) {
    if (map.has(img.src)) continue;
    try { map.set(img.src, await imgData(img.src)); } catch { /* skip unreadable image */ }
  }
  return map;
}

function hf(html, s, pos, ctx) {
  const el = document.createElement("div");
  el.innerHTML = html || "";
  const out = [];
  if (el.textContent.trim() || el.querySelector("img")) group(el, { size: 18, color: "475569" }, ctx, out);
  const pn = s.pageNumbers || {};
  if (pn.enabled && pn.position.startsWith(pos)) {
    const children = [];
    pn.format.split(/(\{n\}|\{total\})/).forEach((p) => {
      if (p === "{n}") children.push(PageNumber.CURRENT);
      else if (p === "{total}") children.push(PageNumber.TOTAL_PAGES);
      else if (p) children.push(p);
    });
    const al = ALIGN[pn.position.split("-")[1]];
    const para = new Paragraph({ alignment: al, children: [new TextRun({ size: 18, color: "475569", children })] });
    pos === "top" ? out.unshift(para) : out.push(para);
  }
  return out;
}

export async function htmlToDocx(html, s) {
  const root = document.createElement("div");
  root.innerHTML = staticize(html, true);
  const hd = document.createElement("div");
  hd.innerHTML = (s.header || "") + (s.footer || "");
  const comments = (s.comments || []).filter((c) => c.text && root.querySelector(`.cmt[data-cid="${c.id}"]`));
  const total = {};
  root.querySelectorAll(".cmt").forEach((a) => { total[a.dataset.cid] = (total[a.dataset.cid] || 0) + 1; });
  const ids = new Map(comments.map((c, i) => [c.id, i + 1]));
  const ctx = { images: await loadImages([root, hd]), inst: 0, rev: 0, cw: dims(s).cw, root, cmt: { ids, total, seen: {} } };
  const out = [];
  group(root, {}, ctx, out);
  const p = PAGE_SIZES[s.size] || PAGE_SIZES.A4;
  const tw = (mm) => Math.round(mm * 56.6929);
  const m = s.margins;
  const fmts = [LevelFormat.DECIMAL, LevelFormat.LOWER_LETTER, LevelFormat.LOWER_ROMAN];
  const doc = new Document({
    comments: {
      children: comments.map((c) => ({
        id: ids.get(c.id), author: c.author || "Author", initials: (c.author || "A").split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 3).toUpperCase(), date: new Date(c.createdAt || Date.now()),
        children: [
          new Paragraph({ children: [new TextRun(`${c.resolved ? "[Resolved] " : ""}${c.text}`)] }),
          ...(c.replies || []).map((r) => new Paragraph({ children: [new TextRun({ text: `${r.author}: `, bold: true }), new TextRun(r.text)] })),
        ],
      })),
    },
    styles: { default: { document: { run: { font: firstFont(themeOf(s).font) || "Calibri", size: 22 } } } },
    numbering: {
      config: [{
        reference: "ol",
        levels: Array.from({ length: 9 }, (_, l) => ({
          level: l, format: fmts[l % 3], text: `%${l + 1}.`, alignment: AlignmentType.START,
          style: { paragraph: { indent: { left: 720 * (l + 1), hanging: 360 } } },
        })),
      }],
    },
    sections: [{
      properties: {
        page: {
          size: { width: tw(p.w), height: tw(p.h), orientation: s.orientation === "landscape" ? PageOrientation.LANDSCAPE : PageOrientation.PORTRAIT },
          margin: { top: tw(m.top), right: tw(m.right), bottom: tw(m.bottom), left: tw(m.left) },
        },
      },
      headers: { default: new Header({ children: hf(s.header, s, "top", ctx) }) },
      footers: { default: new Footer({ children: hf(s.footer, s, "bottom", ctx) }) },
      children: out.length ? out : [new Paragraph({})],
    }],
  });
  return Packer.toBlob(doc);
}
